/**
 * Maps LLM-extracted fields to exact quote form field names and values.
 */

import { BAG_TYPE_OPTIONS, BOX_TYPE_OPTIONS } from './productChatConfig';
import { BAG_MATERIAL_OPTIONS, BAG_PRINTING_FINISHES } from './productFieldOptions';

const BAG_SPEC_OPTIONS = [
  'Rounded Corners',
  'Straight Corners',
  'Valve',
  'Punch Hole',
  'Tear Notch',
  'Sombrero Hole',
];

const PRINTING_STYLES = ['PMS', 'CMYK', 'Both', 'N/A'];
const PRODUCTION_TYPES = ['Full Run', 'Sample Run', 'Full Run and Sample Run both'];

function norm(s) {
  return String(s || '').trim();
}

function normLower(s) {
  return norm(s).toLowerCase();
}

function isTruthyFlag(value) {
  const v = normLower(value);
  return v === 'yes' || v === 'true' || v === '1' || value === true;
}

function pickFuzzy(value, options) {
  const raw = norm(value);
  if (!raw) return '';
  const normalized = options.map((o) => (typeof o === 'string' ? o : (o.value || o.label || o)));

  const exact = normalized.find((o) => norm(o) === raw);
  if (exact) return exact;

  const lower = raw.toLowerCase();
  const fuzzy = normalized.find((o) => {
    const opt = norm(o).toLowerCase();
    return lower === opt || opt.includes(lower) || lower.includes(opt);
  });
  return fuzzy || raw;
}

function normalizeBagMaterial(value) {
  const raw = normLower(value).replace(/\s*\/\s*/g, ' / ');
  const aliases = {
    'pet / vmpet / pe': 'PET / VMPET / PE',
    'pet/vmpet/pe': 'PET / VMPET / PE',
    'pet / pe': 'PET / PE',
    'matte opp / vmpet / pe': 'MATTE OPP / VMPET / PE',
    'pet / al / pe': 'PET / AL / PE',
    'pet / ldpe': 'PET / LDPE',
    'kraft paper / pe': 'KRAFT PAPER / PE',
    'kraft paper / vmpet / pe': 'KRAFT PAPER / VMPET / PE',
    'pe / pe (recyclable)': 'PE / PE (recyclable)',
    'kraft paper / pla (compostable)': 'KRAFT PAPER / PLA (compostable)',
    'matte opp / pet / pe': 'MATTE OPP / PET / PE',
  };
  if (aliases[raw]) return aliases[raw];
  return pickFuzzy(value, BAG_MATERIAL_OPTIONS);
}

function normalizePrintingFinish(value) {
  const raw = normLower(value);
  if (raw.includes('gloss') && raw.includes('lam')) return 'Gloss Lamination';
  if (raw.includes('matte') && raw.includes('lam')) return 'Matte Lamination';
  if (raw.includes('gloss') && raw.includes('var')) return 'Gloss Varnish';
  if (raw.includes('matte') && raw.includes('var')) return 'Matte Varnish';
  if (raw.includes('soft touch')) return 'Soft Touch';
  if (raw.includes('hologram')) return 'Hologram Lamination';
  return pickFuzzy(value, BAG_PRINTING_FINISHES);
}

function resolveBagType(fields) {
  if (fields.bagType) {
    const num = String(fields.bagType).match(/^(\d+)/);
    if (num) return num[1];
    const byLabel = BAG_TYPE_OPTIONS.find((o) =>
      normLower(o.label) === normLower(fields.bagType)
      || normLower(fields.bagType).includes(normLower(o.label)));
    if (byLabel) return byLabel.value;
  }

  const style = norm(fields.style || fields.bagStyle || '');
  const styleNum = style.match(/#?\s*(\d+)/);
  if (styleNum) return styleNum[1];

  const combined = normLower(`${fields.bagType || ''} ${fields.style || ''} ${fields.description || ''}`);
  if (combined.includes('stand up') && combined.includes('zipper')) return '4';
  if (combined.includes('stand up')) return '2';
  if (combined.includes('3 side sealed') && combined.includes('zipper')) return '3';
  if (combined.includes('3 side sealed')) return '1';
  if (combined.includes('flat bottom') && combined.includes('zipper')) return '7';
  if (combined.includes('spout')) return '10';

  return '';
}

function collectBagSpecifications(fields) {
  const specs = new Set(Array.isArray(fields.bagSpecifications) ? fields.bagSpecifications : []);

  const addIf = (flag, name) => {
    if (isTruthyFlag(flag) || normLower(flag) === name.toLowerCase()) specs.add(name);
  };

  addIf(fields.tearNotch, 'Tear Notch');
  addIf(fields.valve, 'Valve');
  addIf(fields.punchHole, 'Punch Hole');
  addIf(fields.sombreroHole, 'Sombrero Hole');
  addIf(fields.roundedCorners, 'Rounded Corners');

  BAG_SPEC_OPTIONS.forEach((name) => {
    if (typeof fields.features === 'string' && fields.features.toLowerCase().includes(name.toLowerCase())) {
      specs.add(name);
    }
  });

  return [...specs].filter((s) => BAG_SPEC_OPTIONS.includes(s));
}

function mapBagFields(raw = {}) {
  const fields = { ...raw };

  fields.bagType = resolveBagType(fields);
  fields.material = normalizeBagMaterial(fields.material || fields.film || fields.substrate);
  fields.width = fields.width || fields.widthMM || fields.widthMm || '';
  fields.height = fields.height || fields.heightMM || fields.heightMm || '';
  fields.gusset = fields.gusset || fields.gussetMM || fields.depth || '';

  if (fields.finish && !fields.printingFinishes) {
    fields.printingFinishes = normalizePrintingFinish(fields.finish);
  } else if (fields.printingFinishes) {
    fields.printingFinishes = normalizePrintingFinish(fields.printingFinishes);
  }

  const zipperText = normLower(fields.zipper || fields.zipperType || '');
  if (zipperText.includes('zipper') || fields.bagType === '3' || fields.bagType === '4' || fields.bagType === '7') {
    fields.closureType = 'Zipper';
    if (!fields.zipperType || fields.zipperType === 'Yes') {
      fields.zipperType = zipperText.includes('regular') ? 'Regular Zipper' : pickFuzzy(fields.zipperType, [
        'Regular Zipper', 'CR Zipper', 'Slider Zipper', 'Double Zipper', 'Pull Tab Zipper', 'Velcro Zipper',
      ]);
    }
  }

  fields.bagSpecifications = collectBagSpecifications(fields);

  if (Array.isArray(fields.printingFeatures) && typeof fields.printingFeatures[0] !== 'string') {
    fields.printingFeatures = [];
  }

  const drop = [
    'style', 'bagStyle', 'finish', 'tearNotch', 'valve', 'zipper', 'punchHole',
    'sombreroHole', 'roundedCorners', 'features', 'film', 'substrate', 'widthMM',
    'heightMM', 'gussetMM', 'widthMm', 'heightMm', 'costPerPiece', 'shippingOptions',
    'description',
  ];
  drop.forEach((k) => delete fields[k]);

  return fields;
}

function resolveBoxType(fields) {
  if (fields.boxType) {
    const num = String(fields.boxType).match(/^(\d+)/);
    if (num) return num[1];
    const byLabel = BOX_TYPE_OPTIONS.find((o) =>
      normLower(o.label).includes(normLower(fields.boxType)));
    if (byLabel) return byLabel.value;
  }
  const styleNum = norm(fields.style).match(/#?\s*(\d+)/);
  return styleNum ? styleNum[1] : fields.boxType || '';
}

function mapBoxFields(raw = {}) {
  const fields = { ...raw };
  fields.boxType = resolveBoxType(fields);
  fields.height = fields.height || fields.heightMM || '';
  fields.width = fields.width || fields.widthMM || '';
  fields.depth = fields.depth || fields.depthMM || '';
  if (fields.finish && !fields.printingFinishes) {
    fields.printingFinishes = normalizePrintingFinish(fields.finish);
  }
  delete fields.style;
  delete fields.finish;
  return fields;
}

function mapLabelFields(raw = {}) {
  const fields = { ...raw };
  fields.shape = fields.shape || fields.labelShape || '';
  if (fields.width || fields.height) {
    fields.size = {
      widthMM: String(fields.width || fields.widthMM || ''),
      heightMM: String(fields.height || fields.heightMM || ''),
    };
  }
  delete fields.labelShape;
  delete fields.width;
  delete fields.height;
  return fields;
}

function extractQuantityTiersFromText(text) {
  if (!text) return [];
  const tiers = [...String(text).matchAll(/([\d.]+)\s*\(([\d,]+)\)/g)];
  if (tiers.length) {
    return tiers.map((m) => parseInt(m[2].replace(/,/g, ''), 10)).filter((n) => n > 0);
  }

  const quantityPhrase = text.match(
    /quantit(?:y|ies)\s+(?:of\s+)?((?:[\d,]+\s*(?:,|and)?\s*){1,}[\d,]+)/i,
  );
  if (quantityPhrase) {
    const nums = [...quantityPhrase[1].matchAll(/([\d,]+)/g)]
      .map((m) => parseInt(m[1].replace(/,/g, ''), 10))
      .filter((n) => n >= 100);
    if (nums.length >= 2) return nums;
  }

  const plain = [...String(text).matchAll(/\b([\d,]{4,})\b/g)];
  return plain.map((m) => parseInt(m[1].replace(/,/g, ''), 10)).filter((n) => n > 0).slice(0, 6);
}

function parseSkuCount(product, contextText = '') {
  const fromSku = parseInt(product.SKU, 10);
  if (Number.isFinite(fromSku) && fromSku > 1) return fromSku;

  const fromAlt = parseInt(product.skuCount, 10);
  if (Number.isFinite(fromAlt) && fromAlt > 1) return fromAlt;

  const haystack = [
    contextText,
    product.specialNotesToProduction,
  ].join(' ');

  const skuMatch = haystack.match(/(\d+)\s*skus?\b/i);
  if (skuMatch) return Math.max(1, parseInt(skuMatch[1], 10));

  return Math.max(1, parseInt(product.SKU, 10) || 1);
}

function parseQuantityValues(values) {
  return values
    .map((val) => {
      if (typeof val === 'number') return val;
      const parsed = parseInt(String(val).replace(/,/g, ''), 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    })
    .filter((val) => val != null);
}

function parseQuantityTiers(product, contextText = '') {
  const fromSku = product.skuQuantities;
  if (Array.isArray(fromSku) && fromSku.length) {
    const firstRow = Array.isArray(fromSku[0]) ? fromSku[0] : [fromSku[0]];
    const tiers = parseQuantityValues(firstRow);
    if (tiers.length) return tiers;
  }

  return extractQuantityTiersFromText(
    [contextText, product.specialNotesToProduction].join(' '),
  );
}

function buildSkuDetailsGrid(skuCount, quantityTiers, existingDetails = []) {
  const tiers = quantityTiers.length ? quantityTiers : [''];
  const template = tiers.map((val, index) => ({
    quantity: index + 1,
    value: val,
  }));

  return Array.from({ length: skuCount }, (_, skuIndex) => {
    const existing = existingDetails[skuIndex];
    if (existing?.quantities?.length) {
      return {
        sku: skuIndex + 1,
        quantities: existing.quantities.map((qty, qtyIndex) => ({
          quantity: qtyIndex + 1,
          value: qty.value ?? template[qtyIndex]?.value ?? '',
        })),
      };
    }

    return {
      sku: skuIndex + 1,
      quantities: template.map((qty) => ({ ...qty })),
    };
  });
}

function applySkuFromText(product, contextText = '') {
  const fromSku = product.skuQuantities;
  let skuCount = parseSkuCount(product, contextText);
  let quantityTiers = parseQuantityTiers(product, contextText);

  if (Array.isArray(fromSku) && fromSku.length > 1) {
    const rows = fromSku.map((row) => (Array.isArray(row) ? row : [row]));
    skuCount = Math.max(skuCount, rows.length);
    const maxTiers = Math.max(...rows.map((row) => row.length), 1);
    product.SKU = skuCount;
    product.quantitiesCount = maxTiers;
    product.skuDetails = rows.map((row, skuIndex) => ({
      sku: skuIndex + 1,
      quantities: row.map((val, qtyIndex) => ({
        quantity: qtyIndex + 1,
        value: typeof val === 'number' ? val : parseInt(String(val).replace(/,/g, ''), 10) || val,
      })),
    }));
    delete product.skuQuantities;
    return product;
  }

  if (quantityTiers.length || skuCount > 1) {
    product.SKU = skuCount;
    product.quantitiesCount = quantityTiers.length || Math.max(1, parseInt(product.quantitiesCount, 10) || 1);
    product.skuDetails = buildSkuDetailsGrid(
      skuCount,
      quantityTiers,
      product.skuDetails || [],
    );

    if (product.specialNotesToProduction) {
      product.specialNotesToProduction = product.specialNotesToProduction
        .replace(/cost per piece:[^|]*/gi, '')
        .replace(/[\d.]+\s*\([\d,]+\)/g, '')
        .replace(/\|\s*/g, ' ')
        .trim();
    }
  }

  delete product.skuQuantities;
  return product;
}

function mapProductFields(product, contextText = '') {
  const type = norm(product.productType);
  let fields = product.fields && typeof product.fields === 'object' ? { ...product.fields } : {};

  if (type === 'Bags') fields = mapBagFields(fields);
  else if (type === 'Boxes') fields = mapBoxFields(fields);
  else if (type === 'Labels') fields = mapLabelFields(fields);

  const printingStyle = pickFuzzy(product.printingStyle, PRINTING_STYLES);
  const productionType = pickFuzzy(product.productionType, PRODUCTION_TYPES);

  let mapped = {
    ...product,
    productType: type,
    printingStyle: PRINTING_STYLES.includes(printingStyle) ? printingStyle : product.printingStyle || '',
    productionType: PRODUCTION_TYPES.includes(productionType) ? productionType : product.productionType || '',
    colorMatch: product.colorMatch === 'Yes' ? 'Yes' : 'No',
    isReprint: product.isReprint === 'Yes' ? 'Yes' : 'No',
    fields,
  };

  mapped = applySkuFromText(mapped, contextText);
  return mapped;
}

export function mapExtractedQuote(extracted = {}, options = {}) {
  const contextText = [
    options.sourceText || '',
    extracted.summary || '',
    extracted.projectDescription || '',
    extracted.notes || '',
  ].join('\n');

  const products = (extracted.products || []).map((product) =>
    mapProductFields(product, contextText),
  );
  let projectDescription = extracted.projectDescription || '';

  if (products[0]?.productType && projectDescription.length > 100) {
    const hasFieldData = Object.keys(products[0].fields || {}).length > 3;
    if (hasFieldData) {
      projectDescription = extracted.projectName
        ? `${extracted.projectName} — imported from document`
        : 'Imported packaging quote';
    }
  }

  return {
    ...extracted,
    projectDescription,
    products: products.length ? products : [{ productType: '', fields: {} }],
  };
}
